import React, { FC, useEffect } from 'react';
import styled, { css } from 'styled-components/macro';

import Actions, { ActionsProps } from 'components/Actions';
import Button from 'components/Button';
import Pictorial from 'components/Pictorial';
import Typography from 'components/Typography';
import Text from 'components/Text';

import media from 'helpers/media';

import Lockbody from 'helpers/lockbody';

const Title = styled((props) => <Typography variant="h5" {...props} />)`
    margin-bottom: ${({ theme }) => theme.space.base};
`;

const ModalPictorial = styled((props) => (
    <Pictorial color="brand" {...props} />
))`
    text-align: center;
    margin-bottom: ${({ theme }) => theme.space.large};

    .Svg_sc {
        width: 90px;
        height: 90px;
    }
`;

const ModalActions = styled((props) => <Actions align="center" {...props} />)`
    &:not(:first-child) {
        margin-top: ${({ theme }) => theme.space.large};
    }
`;

const Content = styled.div`
    position: relative;
    z-index: 2;
    max-width: 585px;
    width: 100%;
    padding: ${({ theme }) => theme.space.xlarge};
    box-sizing: border-box;
    background-color: #fff;
    overflow: auto;
    max-height: 100vh;

    ${({ theme }) => media.mobile`
        padding: ${theme.space.xlarge}  ${theme.space.base};
        min-height: 100vh;
        min-width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
    `};
`;

const Close = styled((props) => (
    <Button theme="secondary" icon="close" iconSize={15} {...props} />
))`
    position: absolute;
    z-index: 10;
    top: ${({ theme }) => theme.space.small};
    right: ${({ theme }) => theme.space.small};

    ${media.mobile`
        position: fixed;
        background-color: #fff;
    `}
`;

const Backdrop = styled.div<{ clickable: boolean }>`
    background-color: rgba(0, 0, 0, 0.8);
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    ${({ clickable }) =>
        clickable &&
        css`
            cursor: pointer;
        `};
`;

const Root = styled.div<{ fixActions: boolean }>`
    position: fixed;
    z-index: 510;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    ${media.mobile`
        display: block;
        overflow: auto;
        -webkit-overflow-scrolling: touch;
    `}

    ${({ fixActions }) =>
        fixActions
            ? `
                padding-bottom: 104px;
                
                ${Title} {
                    position: fixed;
                    top: 16px;
                    left: 16px;
                    right: 49px;
                    text-align: left;
                }

                ${ModalActions} {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    width: 100%;
                    margin-top: 0;
                    padding: 32px 16px;
                    box-sizing: border-box;
                    background-color: #fff;
                }

            `
            : ''}
`;

export type ModalProps = {
    /** The main modalTitle */
    modalTitle: React.ReactNode;

    /** Modal description */
    description?: string;

    /** Fix actions on mobile devices */
    fixMobileActions?: boolean;

    /** Modal custom content */
    content?: React.ReactNode;

    /** Modal optional footer */
    footer?: React.ReactNode;

    /** Object that will be used in the actions component */
    actions?: ActionsProps;

    /** Optional icon name that will be displayed before the modalTitle */
    pictorial?: FC<React.SVGAttributes<SVGElement>>;

    /** Show a close button */
    showCloseButton?: boolean;

    /** Close button click callback */
    onCloseButtonClick?: (e: React.MouseEvent<HTMLElement>) => void;

    /** Allow the modal to be closed by clicking the backdrop */
    canCloseUsingBackdrop?: boolean;

    alignCenter?: boolean;
};

const Modal: FC<ModalProps & React.HTMLAttributes<HTMLDivElement>> = ({
    modalTitle,
    fixMobileActions,
    onCloseButtonClick,
    content,
    description,
    actions,
    pictorial,
    showCloseButton = true,
    canCloseUsingBackdrop = false,
    alignCenter = false,
    footer,
    ...rest
}) => {
    const handleBackdropClick = (e: React.MouseEvent<HTMLElement>) => {
        canCloseUsingBackdrop && onCloseButtonClick && onCloseButtonClick(e);
    };

    useEffect(() => {
        Lockbody();
        return () => {
            Lockbody();
        };
    }, []);

    return (
        <Root {...rest} fixActions={!!fixMobileActions} role="dialog">
            <Backdrop
                clickable={!!canCloseUsingBackdrop}
                onClick={handleBackdropClick}
            />

            <Content>
                {showCloseButton && <Close onClick={onCloseButtonClick} />}
                {pictorial && <ModalPictorial icon={pictorial} />}

                <Text align={alignCenter ? 'center' : 'left'}>
                    <Title>{modalTitle}</Title>
                    {description && (
                        <p className="Modal_Description">{description}</p>
                    )}
                    {content && content}
                </Text>

                {actions && <ModalActions {...actions} />}
                {footer && footer}
            </Content>
        </Root>
    );
};

export default Modal;
